import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Breadcrumbs } from "../../../components/Breadcrumbs";
import type { BreadcrumbItem } from "../../../components/Breadcrumbs";
import { useBreadcrumbBack } from "../../../components/useBreadcrumbBack";
import { PageShell } from "../../shared/layouts/PageShell";
import { PageHeader } from "../../shared/ui/layouts/PageHeader";
import { InfoBanner } from "../../../components/InfoBanner";
import { useAuth } from "../../../providers/AuthProvider";
import { useToast } from "../../../providers/ToastProvider";
import { ConfirmDialog } from "../../../components/ConfirmDialog";
import { ApiError } from "../../../services/apiClient";
import { Avatar } from "../../shared/ui/Avatar";
import { Tabs, type Tab } from "../../../components/ui/Tabs";
import {
  acceptWorkspaceInvitation,
  createWorkspaceInvitation,
  getWorkspace,
  listWorkspaceInvitations,
  listWorkspaceMembers,
  removeWorkspaceMember,
  resendWorkspaceInvitation,
  revokeWorkspaceInvitation,
  type Workspace,
  type WorkspaceInvitationAcceptanceResponse,
  type WorkspaceInvitation,
  type WorkspaceMember
} from "../api/workspaces";

import {
  ArrowClockwise,
  ArrowLeft,
  CheckCircle,
  EnvelopeSimple,
  TrashSimple,
  UserMinus,
  UserPlus,
  UsersThree,
} from "@phosphor-icons/react";
import { defaultIconProps } from "../../shared/ui/icons";
import { FormFieldLabel } from "../../../components/FormFieldLabel";
import { useQueryParamState } from "../../../hooks/useQueryParamState";
import { createLogger } from "../../../utils/logger";

const logger = createLogger("WorkspaceMembersPage");

const pageStackClasses = "flex flex-col gap-8 text-slate-900 dark:text-slate-100";
const cardClasses = "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900";
const placeholderClasses = "px-6 py-12 text-center text-sm text-slate-500 italic dark:text-slate-400";
const alertClasses = "rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const tableWrapperClasses = "overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900";
const tableClasses = "w-full text-left text-sm text-slate-600 dark:text-slate-400";
const actionCellClasses = "text-right whitespace-nowrap";
const ghostButtonClasses = "pm-btn pm-btn-ghost";
const primaryButtonClasses = "pm-btn pm-btn-primary";
const headerSecondaryButtonClasses = "pm-btn pm-btn-secondary";
const dangerButtonClasses = "pm-btn pm-btn-danger pm-btn-xs";
const secondaryButtonClasses = "pm-btn pm-btn-surface pm-btn-xs";
const inputClasses =
  "block w-full rounded-xl border-0 py-2.5 text-slate-900 shadow-sm ring-1 ring-inset ring-slate-200 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6 disabled:bg-slate-50 disabled:text-slate-500 disabled:ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-700 dark:placeholder-slate-500 dark:disabled:bg-slate-800";

export function WorkspaceMembersPage() {
  const { t } = useTranslation();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const location = useLocation();
  const { accessToken, user } = useAuth();
  const toast = useToast();

  const initialWorkspace = (location.state as { workspace?: Workspace } | undefined)?.workspace;

  const [workspace, setWorkspace] = useState<Workspace | undefined>(initialWorkspace);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [invitations, setInvitations] = useState<WorkspaceInvitation[]>([]);
  const [activeTab, setActiveTab] = useQueryParamState<"members" | "invites">({
    key: "tab",
    defaultValue: "members",
    parse: (value) => (value === "invites" || value === "members" ? value : null),
    serialize: (value) => value,
    replace: true,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [processingInviteId, setProcessingInviteId] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<
    | { type: "revoke-invite"; invitation: WorkspaceInvitation }
    | { type: "remove-member"; member: WorkspaceMember }
    | null
  >(null);

  const breadcrumbItems = useMemo<BreadcrumbItem[]>(() => {
    const items: BreadcrumbItem[] = [
      { label: t("workspaces.my_workspaces.title"), to: "/workspaces" },
      { label: workspace ? workspace.name : t("workspaces.members.title_prefix", { name: "" }).trim() }
    ];
    items.push({ label: t("workspaces.members.members_tab") });
    return items;
  }, [workspace, t]);

  const canManage = Boolean(
    workspace &&
    user &&
    (workspace.role === "owner" || workspace.owner_id === user.id)
  );

  const handleBack = useBreadcrumbBack({
    items: breadcrumbItems,
    fallback: { to: "/workspaces" },
  });
  
  const handleViewInvites = useCallback(() => {
    setActiveTab("invites");
  }, [setActiveTab]);

  const loadWorkspace = useCallback(async () => {
    if (!accessToken || !workspaceId || workspace) {
      return;
    }
    try {
      const details = await getWorkspace(accessToken, workspaceId);
      setWorkspace(details);
    } catch (err) {
      logger.warn("Failed to load workspace", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, workspaceId]);

  const loadData = useCallback(async () => {
    if (!accessToken || !workspaceId) {
      return;
    }
    setIsLoading(true);
    setPageError(null);
    try {
      const [memberList, inviteList] = await Promise.all([
        listWorkspaceMembers(accessToken, workspaceId),
        listWorkspaceInvitations(accessToken, workspaceId)
      ]);
      setMembers(Array.isArray(memberList) ? memberList : []);
      setInvitations(Array.isArray(inviteList) ? inviteList : []);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : t("workspaces.members.load_error");
      setPageError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, workspaceId, t, toast]);

  useEffect(() => {
    void loadWorkspace();
  }, [loadWorkspace]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const resolveInvitationErrorMessage = useCallback(
    (error: unknown, fallback: string) => {
      if (error instanceof ApiError) {
        const detailCode = error.body?.details?.code;
        if (
          typeof detailCode === "string" &&
          detailCode === "user_ineligible_for_invite"
        ) {
          return t("workspaces.members.ineligible_error");
        }
        return error.message;
      }
      return fallback;
    },
    [t],
  );

  const handleInvite = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accessToken || !workspaceId || !inviteEmail.trim()) {
      return;
    }
    setIsSendingInvite(true);
    setPageError(null);
    try {
      const invitation = await createWorkspaceInvitation(
        accessToken,
        workspaceId,
        inviteEmail.trim()
      );
      setInvitations((prev) => [invitation, ...prev]);
      setInviteEmail("");
      toast.success(t("workspaces.members.send_success", { email: inviteEmail.trim() }));
    } catch (err) {
      const message = resolveInvitationErrorMessage(
        err,
        t("workspaces.members.send_error"),
      );
      toast.error(message);
    } finally {
      setIsSendingInvite(false);
    }
  };

  const handleResend = async (invitation: WorkspaceInvitation) => {
    if (!accessToken || !workspaceId) {
      return;
    }
    setProcessingInviteId(invitation.id);
    setPageError(null);
    try {
      const updated = await resendWorkspaceInvitation(
        accessToken,
        workspaceId,
        invitation.id
      );
      setInvitations((prev) =>
        prev.map((item) => (item.id === invitation.id ? updated : item))
      );
      toast.success(t("workspaces.members.resend_success", { email: invitation.email }));
    } catch (err) {
      const message = resolveInvitationErrorMessage(
        err,
        t("workspaces.members.resend_error"),
      );
      toast.error(message);
    } finally {
      setProcessingInviteId(null);
    }
  };

  const handleRevoke = useCallback(
    async (invitation: WorkspaceInvitation) => {
      if (!accessToken || !workspaceId) {
        return;
      }
      setProcessingInviteId(invitation.id);
      setPageError(null);
      try {
        await revokeWorkspaceInvitation(accessToken, workspaceId, invitation.id);
        setInvitations((prev) => prev.filter((item) => item.id !== invitation.id));
        toast.success(t("workspaces.members.revoke_success"));
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : t("workspaces.members.revoke_error");
        toast.error(message);
      } finally {
        setProcessingInviteId(null);
      }
    },
    [accessToken, workspaceId, setPageError, setInvitations, toast, t]
  );

  const handleRemoveMember = useCallback(
    async (member: WorkspaceMember) => {
      if (!accessToken || !workspaceId) {
        return;
      }
      const displayName = member.user?.name ?? member.user?.email ?? t("workspaces.members.member_fallback");
      setRemovingMemberId(member.user_id);
      setPageError(null);
      try {
        await removeWorkspaceMember(accessToken, workspaceId, member.user_id);
        setMembers((prev) => prev.filter((item) => item.user_id !== member.user_id));
        toast.success(
          t("workspaces.members.remove_success", {
            name: displayName
          })
        );
      } catch (err) {
        const message =
          err instanceof ApiError ? err.message : t("workspaces.members.remove_error");
        toast.error(message);
      } finally {
        setRemovingMemberId(null);
      }
    },
    [accessToken, workspaceId, setPageError, toast, t]
  );

  const sortedMembers = useMemo(() => {
    return [...members].sort((a, b) =>
      new Date(a.joined_at).getTime() - new Date(b.joined_at).getTime()
    );
  }, [members]);

  const confirmDialogConfig = useMemo(() => {
    if (!confirmAction) {
      return null;
    }
    if (confirmAction.type === "revoke-invite") {
      return {
        title: t("workspaces.members.revoke_confirm", {
          email: confirmAction.invitation.email
        }),
        confirmLabel: t("workspaces.members.revoke_button"),
        loading: processingInviteId === confirmAction.invitation.id,
        action: () => handleRevoke(confirmAction.invitation)
      };
    }
    const displayName =
      confirmAction.member.user?.name ??
      confirmAction.member.user?.email ??
      t("workspaces.members.member_fallback");
    return {
      title: t("workspaces.members.remove_confirm", { name: displayName }),
      confirmLabel: t("workspaces.members.remove_button"),
      loading: removingMemberId === confirmAction.member.user_id,
      action: () => handleRemoveMember(confirmAction.member)
    };
  }, [confirmAction, handleRevoke, handleRemoveMember, processingInviteId, removingMemberId, t]);

  const handleConfirmAction = useCallback(async () => {
    if (!confirmDialogConfig) {
      return;
    }
    try {
      await confirmDialogConfig.action();
    } finally {
      setConfirmAction(null);
    }
  }, [confirmDialogConfig]);

  const sortedInvitations = useMemo(() => {
    return [...invitations].sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [invitations]);

  const tabs = useMemo<Tab[]>(() => [
    { id: "members", label: t("workspaces.members.members_tab"), count: sortedMembers.length },
    { id: "invites", label: t("workspaces.members.invitations_tab"), count: sortedInvitations.length },
  ], [sortedMembers.length, sortedInvitations.length, t]);

  if (!user || !accessToken || !workspaceId) {
    return null;
  }

  const headerTitle = workspace
    ? t("workspaces.members.title_prefix", { name: workspace.name })
    : t("workspaces.members.title_prefix", { name: "" }).trim();
  const headerSubtitle = workspace?.description;
  const viewInvitesLabel = t("workspaces.members.view_invites_button");
  const backLabel = t("actions.back");

  return (
    <PageShell className={pageStackClasses} width="wide">
      <WorkspaceMembersHeader
        breadcrumbs={breadcrumbItems}
        title={headerTitle}
        subtitle={headerSubtitle}
        onViewInvites={handleViewInvites}
        onBack={handleBack}
        viewInvitesLabel={viewInvitesLabel}
        backLabel={backLabel}
      />

      <InfoBanner
        title="What are Members?"
        description={t("workspaces.members.subtitle")}
        tone="info"
        icon={<UsersThree {...defaultIconProps} aria-hidden />}
        dismissId="workspace-members-banner"
        dismissDurationS={60 * 60 * 24 * 7}
      />

      {pageError && (
        <div className={alertClasses} role="alert">
          {pageError}
        </div>
      )}

      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onChange={(id) => setActiveTab(id as "members" | "invites")}
        swipeable
        renderContent={(tabId) => {
          if (isLoading) {
            return <p className={placeholderClasses}>{t("workspaces.members.loading")}</p>;
          }

          if (tabId === "members") {
            return (
              <section aria-label="Workspace members" className="flex flex-col pt-6">
                {sortedMembers.length === 0 ? (
                  <p className={placeholderClasses}>
                    {t("workspaces.members.members_empty")}
                  </p>
                ) : (
                  <div className={tableWrapperClasses}>
                    <table className={tableClasses}>
                      <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                        <tr>
                          <th scope="col" className="px-6 py-3">{t("workspaces.members.table_name")}</th>
                          <th scope="col" className="px-6 py-3">{t("workspaces.members.table_email")}</th>
                          <th scope="col" className="px-6 py-3">{t("workspaces.members.table_role")}</th>
                          <th scope="col" className="px-6 py-3">{t("workspaces.members.table_joined")}</th>
                          {canManage && <th scope="col" className={`px-6 py-3 ${actionCellClasses}`}>{t("workspaces.members.table_actions")}</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {sortedMembers.map((member) => (
                          <tr key={member.user_id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <Avatar
                                  name={member.user?.name}
                                  email={member.user?.email}
                                  photoUrl={
                                    member.user?.profile_photo?.download_url ?? null
                                  }
                                  fallbackUrl={member.user?.profile_photo?.remote_url ?? null}
                                  size={36}
                                  className="shrink-0"
                                />
                                <div className="flex flex-col">
                                  <strong className="text-sm font-medium text-slate-900 dark:text-slate-100">{member.user?.name ?? t("workspaces.members.member_fallback")}</strong>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              {member.user?.email ?? "—"}
                            </td>
                            <td className="px-6 py-4">
                              {renderRoleBadge(member.role)}
                            </td>
                            <td className="px-6 py-4 text-slate-500">
                              {new Date(member.joined_at).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric"
                              })}
                            </td>
                            {canManage ? (
                              <td className={`px-6 py-4 ${actionCellClasses}`}>
                                {member.role === "owner" || member.user_id === user.id ? (
                                  <span className="text-xs text-slate-400 italic">{t("common.table.empty_value")}</span>
                                ) : (
                                  <button
                                    type="button"
                                    className={dangerButtonClasses}
                                    onClick={() => setConfirmAction({ type: "remove-member", member })}
                                    disabled={removingMemberId === member.user_id}
                                  >
                                    {removingMemberId === member.user_id ? (
                                      t("workspaces.members.removing")
                                    ) : (
                                      <>
                                        <UserMinus {...defaultIconProps} aria-hidden />
                                        {t("workspaces.members.remove_button")}
                                      </>
                                    )}
                                  </button>
                                )}
                              </td>
                            ) : null}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          }

          if (tabId === "invites") {
            return (
              <section aria-label="Pending invitations" className="flex flex-col gap-6 pt-6">
                {canManage ? (
                  <form className={`${cardClasses} flex flex-col gap-4`} onSubmit={handleInvite}>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-3">
                        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-600 shadow-sm ring-1 ring-sky-100 dark:bg-sky-900/40 dark:text-sky-300 dark:ring-sky-900/50">
                          <UserPlus {...defaultIconProps} aria-hidden />
                        </span>
                        <div className="flex flex-col gap-1">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            {t("workspaces.members.invite_label")}
                          </span>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {t("workspaces.members.invite_helper")}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                      <div className="flex-1">
                        <label htmlFor="invite-email">
                          <FormFieldLabel label={t("workspaces.members.invite_label")} />
                        </label>
                        <input
                          id="invite-email"
                          type="email"
                          inputMode="email"
                          placeholder={t("workspaces.members.invite_placeholder")}
                          value={inviteEmail}
                          onChange={(event) => setInviteEmail(event.target.value)}
                          required
                          disabled={isSendingInvite}
                          className={`${inputClasses} mt-2`}
                        />
                      </div>
                      <div className="flex items-end">
                        <button type="submit" disabled={isSendingInvite} className={primaryButtonClasses}>
                          {isSendingInvite ? (
                            t("workspaces.members.sending")
                          ) : (
                            <>
                              <UserPlus {...defaultIconProps} aria-hidden />
                              {t("workspaces.members.send_button")}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </form>
                ) : (
                  <p className={placeholderClasses}>
                    {t("workspaces.members.invites_permission")}
                  </p>
                )}

                {sortedInvitations.length === 0 ? (
                  <p className={placeholderClasses}>
                    {t("workspaces.members.invites_empty")}
                  </p>
                ) : (
                  <div className={tableWrapperClasses}>
                    <table className={tableClasses}>
                      <thead className="bg-slate-50 text-xs font-semibold uppercase text-slate-500">
                        <tr>
                          <th scope="col" className="px-6 py-3">{t("workspaces.members.table_email")}</th>
                          <th scope="col" className="px-6 py-3">{t("workspaces.members.table_status")}</th>
                          <th scope="col" className="px-6 py-3">{t("workspaces.members.table_expires")}</th>
                          {canManage && <th scope="col" className={`px-6 py-3 ${actionCellClasses}`}>{t("workspaces.members.table_actions")}</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                        {sortedInvitations.map((invitation) => (
                          <tr key={invitation.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50">
                            <td className="px-6 py-4 font-medium text-slate-900 dark:text-slate-100">{invitation.email}</td>
                            <td className="px-6 py-4">
                              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-800 capitalize dark:bg-slate-800 dark:text-slate-300">
                                {invitation.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-slate-500">
                              {new Date(invitation.expires_at).toLocaleDateString(undefined, {
                                year: "numeric",
                                month: "short",
                                day: "numeric"
                              })}
                            </td>
                            {canManage && (
                              <td className={`px-6 py-4 ${actionCellClasses}`}>
                                <div className="flex items-center justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleResend(invitation)}
                                    disabled={processingInviteId === invitation.id}
                                    className={secondaryButtonClasses}
                                  >
                                    {processingInviteId === invitation.id ? (
                                      t("workspaces.members.resending")
                                    ) : (
                                      <>
                                        <ArrowClockwise {...defaultIconProps} aria-hidden />
                                        {t("workspaces.members.resend_button")}
                                      </>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    className={dangerButtonClasses}
                                    onClick={() => setConfirmAction({ type: "revoke-invite", invitation })}
                                    disabled={processingInviteId === invitation.id}
                                  >
                                    {processingInviteId === invitation.id ? (
                                      t("workspaces.members.working")
                                    ) : (
                                      <>
                                        <TrashSimple {...defaultIconProps} aria-hidden />
                                        {t("workspaces.members.revoke_button")}
                                      </>
                                    )}
                                  </button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          }
          return null;
        }}
      />

      <ConfirmDialog
        open={Boolean(confirmDialogConfig)}
        title={confirmDialogConfig?.title ?? ""}
        confirmLabel={confirmDialogConfig?.confirmLabel ?? ""}
        cancelLabel={t("actions.cancel")}
        confirmTone="danger"
        confirmLoading={Boolean(confirmDialogConfig?.loading)}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
      />
    </PageShell>
  );
}

type WorkspaceMembersHeaderProps = {
  breadcrumbs: BreadcrumbItem[];
  title: string;
  subtitle?: string | null;
  onViewInvites: () => void;
  onBack: () => void;
  viewInvitesLabel: string;
  backLabel: string;
};

const WorkspaceMembersHeader = memo(function WorkspaceMembersHeader({
  breadcrumbs,
  title,
  subtitle,
  onViewInvites,
  onBack,
  viewInvitesLabel,
  backLabel,
}: WorkspaceMembersHeaderProps) {
  return (
    <>
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        className="flex flex-col gap-4"
        title={title}
        subtitle={subtitle}
        actions={
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onViewInvites}
              className={headerSecondaryButtonClasses}
            >
              <EnvelopeSimple {...defaultIconProps} aria-hidden />
              {viewInvitesLabel}
            </button>
            <button type="button" onClick={onBack} className={ghostButtonClasses}>
              <ArrowLeft {...defaultIconProps} aria-hidden />
              {backLabel}
            </button>
          </div>
        }
      />
    </>
  );
});

export function WorkspaceInvitationAcceptButton({
  token,
  onAccepted
}: {
  token: string;
  onAccepted?: (response: WorkspaceInvitationAcceptanceResponse) => void;
}) {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClick = async () => {
    if (!accessToken) {
      setError(t("workspaces.members.accept_signin_required"));
      return;
    }
    setIsProcessing(true);
    setError(null);
    try {
      const result = await acceptWorkspaceInvitation(accessToken, token);
      onAccepted?.(result);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : t("workspaces.members.accept_error");
      setError(message);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 items-center">
      <button
        type="button"
        className={primaryButtonClasses}
        onClick={handleClick}
        disabled={isProcessing}
      >
        {isProcessing ? (
          t("workspaces.members.accepting")
        ) : (
          <>
            <CheckCircle {...defaultIconProps} aria-hidden />
            {t("workspaces.members.accept_button")}
          </>
        )}
      </button>
      {error && <p className="text-sm text-rose-600">{error}</p>}
    </div>
  );
}
  const renderRoleBadge = (role: WorkspaceMember["role"]) => {
    const normalized = (role ?? "viewer").toLowerCase();
    const label = normalized.charAt(0).toUpperCase() + normalized.slice(1);
    const baseClasses = "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border";
    const variantClasses = normalized === "owner"
        ? "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-900/50"
        : normalized === "editor"
        ? "bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-900/50"
        : "bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
    return <span className={`${baseClasses} ${variantClasses}`}>{label}</span>;
  };
